import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge, Button, Card, ConfirmDialog, LoadingList, SectionHeader, cx,
} from '@timely/ui';
import {
    CAPABILITY_AREAS, capabilitiesIn, capabilityDefinition, holds, isGrantable,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { supabase } from '../lib/supabaseClient';
import { writeFailure } from '../team/useTeam';

/**
 * The role editor.
 *
 * A role is a named set of capabilities, and this is where the set is chosen.
 * Three restrictions are visible on the screen, and all three are enforced in
 * the database whatever this screen does:
 *
 *  - the owner role is not editable and not deletable, by anybody. It is the
 *    only holder of `workspace.delete`, and that capability is not grantable
 *    to any other role — so it is not offered here at all;
 *  - nobody may grant a capability they do not themselves hold. The checkboxes
 *    for those are disabled and say so, rather than being hidden, because "why
 *    can I not give them that" is a question worth answering once;
 *  - a role with members cannot be deleted until they are moved.
 *
 * `guard_role_capabilities` in migration 0004 refuses all three at the
 * database, and the audit trigger in 0011 records the capability set before and
 * after every change — so the history of who was given what survives the
 * screen entirely.
 */

interface Role {
    id: string;
    name: string;
    isOwner: boolean;
    isSystem: boolean;
    capabilities: string[];
    memberCount: number;
}

export function Roles() {
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const canManage = holds(context, 'roles.manage');

    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [editing, setEditing] = useState<Role | null>(null);
    const [deleting, setDeleting] = useState<Role | null>(null);
    const [busy, setBusy] = useState(false);

    /** What this person may hand out. An owner holds everything, listed or not. */
    const grantable = useMemo(() => {
        if (context.membership.isOwner) {
            return new Set(
                CAPABILITY_AREAS.flatMap(a => capabilitiesIn(a.area).map(c => c.code))
                    .filter(isGrantable),
            );
        }
        return new Set(context.membership.capabilities.filter(isGrantable));
    }, [context.membership]);

    const load = useCallback(async () => {
        setLoading(true);
        const client = supabase();
        const [rolesResult, membersResult] = await Promise.all([
            client.from('workspace_roles')
                .select('id, name, is_owner, is_system, capabilities')
                .eq('workspace_id', workspaceId)
                .order('is_owner', { ascending: false })
                .order('name'),
            client.from('workspace_members')
                .select('role_id').eq('workspace_id', workspaceId).eq('status', 'active'),
        ]);

        if (rolesResult.error) {
            setNotice({ tone: 'error', text: 'We could not load the roles. Try again in a moment.' });
            setLoading(false);
            return;
        }

        const counts = new Map<string, number>();
        for (const row of (membersResult.data ?? []) as { role_id: string }[]) {
            counts.set(row.role_id, (counts.get(row.role_id) ?? 0) + 1);
        }

        setRoles(((rolesResult.data ?? []) as {
            id: string; name: string; is_owner: boolean; is_system: boolean; capabilities: string[] | null;
        }[]).map(r => ({
            id: r.id,
            name: r.name,
            isOwner: r.is_owner,
            isSystem: r.is_system,
            capabilities: r.capabilities ?? [],
            memberCount: counts.get(r.id) ?? 0,
        })));
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    const save = async (role: Role, name: string, capabilities: string[]) => {
        setBusy(true);
        setNotice(null);
        const client = supabase();
        const isNew = role.id === '';
        const { error } = isNew
            ? await client.from('workspace_roles')
                .insert({ workspace_id: workspaceId, name, capabilities })
            : await client.from('workspace_roles')
                .update({ name, capabilities }).eq('id', role.id);

        setBusy(false);
        if (error) {
            setNotice({ tone: 'error', text: writeFailure(error.message) });
            return;
        }
        setEditing(null);
        await load();
        setNotice({ tone: 'ok', text: isNew ? `“${name}” has been created.` : `“${name}” has been saved.` });
    };

    return (
        <Page>
            <SectionHeader
                title="Roles"
                subtitle="A role is a set of permissions. People are put into roles on the Team screen."
                actions={canManage ? (
                    <Button
                        variant="primary"
                        onClick={() => setEditing({
                            id: '', name: '', isOwner: false, isSystem: false,
                            capabilities: [], memberCount: 0,
                        })}
                    >
                        New role
                    </Button>
                ) : undefined}
            />

            {notice && (
                <div className="mt-4">
                    <FormNotice tone={notice.tone}>{notice.text}</FormNotice>
                </div>
            )}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading the roles" rows={5} />
                ) : (
                    <ul className="divide-y divide-separator">
                        {roles.map(role => (
                            <li key={role.id} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[14.5px] font-semibold text-label">{role.name}</span>
                                        {role.isOwner && <Badge tone="brand">Owner</Badge>}
                                        {role.isSystem && !role.isOwner && <Badge>Built in</Badge>}
                                    </div>
                                    <div className="text-[13px] text-label-3 mt-0.5">
                                        {role.isOwner
                                            ? 'Everything, including deleting the company. Not editable.'
                                            : `${role.capabilities.length} permission${role.capabilities.length === 1 ? '' : 's'}`}
                                        {' · '}
                                        {role.memberCount === 0
                                            ? 'nobody in it'
                                            : `${role.memberCount} ${role.memberCount === 1 ? 'person' : 'people'}`}
                                    </div>
                                </div>

                                {canManage && !role.isOwner && (
                                    <div className="flex items-center gap-2 flex-none">
                                        <Button size="sm" variant="secondary" onClick={() => setEditing(role)}>
                                            Edit
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="quiet"
                                            disabled={role.isSystem || role.memberCount > 0}
                                            title={
                                                role.isSystem ? 'A built-in role cannot be deleted.'
                                                    : role.memberCount > 0 ? 'Move its people to another role first.'
                                                        : undefined
                                            }
                                            onClick={() => setDeleting(role)}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {editing && (
                <RoleEditor
                    role={editing}
                    grantable={grantable}
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={(name, capabilities) => { void save(editing, name, capabilities); }}
                />
            )}

            <ConfirmDialog
                open={deleting !== null}
                title="Delete this role?"
                confirmLabel="Delete role"
                busy={busy}
                onCancel={() => setDeleting(null)}
                onConfirm={() => {
                    const role = deleting;
                    setDeleting(null);
                    if (!role) return;
                    void (async () => {
                        setBusy(true);
                        const { error } = await supabase().from('workspace_roles').delete().eq('id', role.id);
                        setBusy(false);
                        if (error) {
                            setNotice({ tone: 'error', text: writeFailure(error.message) });
                            return;
                        }
                        await load();
                        setNotice({ tone: 'ok', text: `“${role.name}” has been deleted.` });
                    })();
                }}
            >
                <p><strong>{deleting?.name}</strong> will be removed. Nobody is in it, so nobody loses access.</p>
            </ConfirmDialog>
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

function RoleEditor({
    role, grantable, busy, onCancel, onSave,
}: {
    role: Role;
    grantable: Set<string>;
    busy: boolean;
    onCancel: () => void;
    onSave: (name: string, capabilities: string[]) => void;
}) {
    const [name, setName] = useState(role.name);
    const [chosen, setChosen] = useState<Set<string>>(new Set(role.capabilities));
    const [problem, setProblem] = useState<string | null>(null);

    const toggle = (code: string) => {
        setChosen(previous => {
            const next = new Set(previous);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    return (
        <Card className="mt-6 px-5 py-5">
            <h3 className="text-[16px] font-bold text-label tracking-[-0.02em]">
                {role.id ? `Edit “${role.name}”` : 'New role'}
            </h3>

            <div className="mt-4 max-w-sm">
                <label htmlFor="role-name" className="block text-[13px] font-semibold text-label mb-1.5">
                    Name
                </label>
                <input
                    id="role-name"
                    value={name}
                    onChange={event => setName(event.target.value)}
                    className="w-full h-11 px-3.5 rounded-[11px] bg-card border border-separator-strong text-[15px] text-label outline-none focus:border-accent"
                />
                {problem && <div className="text-[12.5px] text-dang mt-1.5" role="alert">{problem}</div>}
            </div>

            <p className="text-[13px] text-label-3 mt-5 leading-relaxed max-w-prose">
                You can only give a role permissions you hold yourself. The rest are shown, and off, so
                that what this role does not have is as clear as what it does.
            </p>

            <div className="mt-4 grid gap-5 sm:grid-cols-2">
                {CAPABILITY_AREAS.map(({ area, title }) => {
                    const capabilities = capabilitiesIn(area).filter(c => isGrantable(c.code));
                    if (capabilities.length === 0) return null;
                    return (
                        <fieldset key={area} className="min-w-0">
                            <legend className="text-[13px] font-bold text-label mb-2">{title}</legend>
                            <div className="space-y-1.5">
                                {capabilities.map(capability => {
                                    const allowed = grantable.has(capability.code);
                                    const id = `cap-${capability.code}`;
                                    return (
                                        <div key={capability.code} className="flex items-start gap-2.5">
                                            <input
                                                id={id}
                                                type="checkbox"
                                                className="mt-[3px] flex-none"
                                                checked={chosen.has(capability.code)}
                                                disabled={!allowed}
                                                onChange={() => toggle(capability.code)}
                                            />
                                            <label
                                                htmlFor={id}
                                                className={cx(
                                                    'text-[13.5px] leading-snug',
                                                    allowed ? 'text-label' : 'text-label-3',
                                                )}
                                            >
                                                {capability.label}
                                                {!allowed && (
                                                    <span className="block text-[12px] text-label-3">
                                                        You do not hold this yourself.
                                                    </span>
                                                )}
                                                {allowed && capability.description && (
                                                    <span className="block text-[12px] text-label-3">
                                                        {capability.description}
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </fieldset>
                    );
                })}
            </div>

            {/* Anything the role already carries that this build does not know
                about — a capability from a newer migration, seen by an older
                deployment. Shown rather than silently dropped on save. */}
            {[...chosen].filter(c => !capabilityDefinition(c)).length > 0 && (
                <p className="text-[13px] text-label-3 mt-5">
                    This role also carries {[...chosen].filter(c => !capabilityDefinition(c)).join(', ')},
                    which this version does not recognise. It will be kept as it is.
                </p>
            )}

            <div className="mt-6 flex items-center gap-2">
                <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => {
                        const trimmed = name.trim();
                        if (trimmed.length === 0) {
                            setProblem('Give the role a name.');
                            return;
                        }
                        setProblem(null);
                        onSave(trimmed, [...chosen]);
                    }}
                >
                    {busy ? 'Saving…' : role.id ? 'Save role' : 'Create role'}
                </Button>
                <Button variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
            </div>
        </Card>
    );
}
